import { createContext, useContext } from 'react';

export interface FieldContextValue {
  /** id given to the control, and to the label's `htmlFor`. */
  id: string;
  /** Space-separated ids of the description and/or error text, if any. */
  describedBy: string | undefined;
  /** True when the field is showing an error. */
  invalid: boolean;
}

export const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * Raw field context. `null` when the control is rendered outside a
 * `<FormField>`, which is legal — the control just wires nothing up.
 */
export function useFieldContext(): FieldContextValue | null {
  return useContext(FieldContext);
}

export interface FieldControlProps {
  id: string | undefined;
  'aria-describedby': string | undefined;
  'aria-invalid': boolean | undefined;
}

/**
 * The accessibility props a control inside a `<FormField>` must spread onto
 * itself. Explicit props passed by the caller always win.
 */
export function useFieldProps(overrides?: Partial<FieldControlProps>): FieldControlProps {
  const field = useFieldContext();

  const describedBy = [overrides?.['aria-describedby'], field?.describedBy]
    .filter((value): value is string => Boolean(value))
    .join(' ');

  return {
    id: overrides?.id ?? field?.id,
    'aria-describedby': describedBy === '' ? undefined : describedBy,
    'aria-invalid': overrides?.['aria-invalid'] ?? (field?.invalid ? true : undefined),
  };
}
